import React, { useState, useEffect } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import { ReactComponent as EmptyCheckBoxIcon } from 'src/Images/icons/common/empty-check-box.svg';
import { ReactComponent as FilleCheckBox } from 'src/Images/icons/common/filledCheckBox.svg';
import { ReactComponent as StringIcon } from 'src/Images/icons/common/stringIcon.svg';
import { ReactComponent as LengthIcon } from 'src/Images/icons/common/LengthIcon.svg';
import { ReactComponent as ToggleIcon } from 'src/Images/icons/common/toggleIcon.svg';
import { ReactComponent as Close } from 'src/Images/icons/common/close.svg';
import getColorCode from 'src/utils/GetThemeColor';

export default function QualitycheckTargetModal({
  addTargetValueModal,
  setAddTargetValueModal,
  activeDimension,
  numericType,
  handleNumericTypeChange,
  formData,
  handleAddDimensionValue,
  getUpdatedOptions,
  themeColor,
}) {
  const [draftValues, setDraftValues] = useState({
    targetMinValue: '',
    targetMaxValue: '',
    targetValue: '',
  });

  useEffect(() => {
    if (!addTargetValueModal || !activeDimension) return;

    const dim = formData.dimensions?.find(
      (dimensionObj) => dimensionObj.dimension_id === activeDimension.id,
    );

    if (dim) {
      setDraftValues({
        targetMinValue: dim.targetMinValue || '',
        targetMaxValue: dim.targetMaxValue || '',
        targetValue: dim.targetValue || '',
      });
    } else {
      setDraftValues({
        targetMinValue: '',
        targetMaxValue: '',
        targetValue: '',
      });
    }
  }, [activeDimension, formData, addTargetValueModal]);

  return (
    <Modal
      show={addTargetValueModal}
      onHide={() => setAddTargetValueModal(false)}
      centered
      size="md"
      dialogClassName="custom-modal"
      backdrop="static"
      keyboard={false}
    >
      {(activeDimension?.data_type?.id === 1 ||
        activeDimension?.data_type?.id === 2) && (
        <Modal.Body>
          <div className="div-flex-column rg-10">
            {/* Header */}
            <div className="div-flex-row div-space-between div-align-center padding-bottom-10 border-bottom cg-10">
              <div className="div-flex-row div-align-center cg-5 quality-check-icon-padding border-radius-4">
                <LengthIcon
                  className="icon-size-big"
                  color={getColorCode(themeColor)}
                />
                <span className="text-lg text-semi-bold">
                  {activeDimension?.data_type?.name}
                </span>
              </div>
              <button
                className="icon-btn-none"
                onClick={() => setAddTargetValueModal(false)}
                aria-label="Close"
              >
                <Close className="quality-check-modal-icon" />
              </button>
            </div>
            {/* Radio Select */}
            <div className="div-flex-row cg-20 margin-top-5">
              <div className="div-flex-row cg-5">
                <input
                  type="radio"
                  name="numericType"
                  value="range"
                  checked={numericType === 'range'}
                  onChange={() => handleNumericTypeChange('range')}
                />
                <span className="text-sm">Select Range</span>
              </div>
              <div className="div-flex-row cg-5">
                <input
                  type="radio"
                  name="numericType"
                  value="value"
                  checked={numericType === 'value'}
                  onChange={() => handleNumericTypeChange('value')}
                />
                <span className="text-sm">Select Value</span>
              </div>
            </div>

            {/* Input Fields */}
            <Row className="margin-top-10 quality-check-modal-height">
              {numericType === 'range' && (
                <div className="div-flex-column rg-10">
                  {/* MIN & MAX Inputs */}
                  <div className="div-space-between div-flex-row cg-10">
                    {/* Min */}
                    <div className="w-100">
                      <span className="text-sm">Minimum value</span>
                      <div className="quality-check-input-group w-100">
                        <input
                          className="quality-check-input-field text-sm"
                          placeholder="Enter min value"
                          type="number"
                          value={draftValues.targetMinValue}
                          onWheel={(e) => e.target.blur()}
                          onChange={(e) => {
                            const newMin = e.target.value;
                            const newTemp = {
                              ...draftValues,
                              targetMinValue: newMin,
                            };
                            if (newMin === '') {
                              newTemp.targetValue = '';
                            }
                            setDraftValues(newTemp);
                          }}
                        />
                      </div>
                    </div>
                    <div className="padding-top-10 div-align-center">
                      <span className="text-md text-grey padding-top-10 div-align-center">
                        to
                      </span>
                    </div>
                    {/* Max */}
                    <div className="w-100">
                      <span className="text-sm">Maximum value</span>
                      <div className="quality-check-input-group w-100">
                        <input
                          className={`quality-check-input-field text-sm ${(() => {
                            const minStr = draftValues.targetMinValue;
                            const maxStr = draftValues.targetMaxValue;
                            if (!minStr || !maxStr) return '';
                            return Number(maxStr) < Number(minStr)
                              ? 'form-input-error'
                              : '';
                          })()}`}
                          placeholder="Enter max value"
                          type="number"
                          value={draftValues.targetMaxValue}
                          onWheel={(e) => e.target.blur()}
                          onChange={(e) => {
                            const newMax = e.target.value;
                            const newTemp = {
                              ...draftValues,
                              targetMaxValue: newMax,
                            };
                            if (newMax === '') {
                              newTemp.targetValue = '';
                            }
                            setDraftValues(newTemp);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  {/* VALUE Input */}
                  <div>
                    <span className="text-sm">Target value</span>
                    <div className="quality-check-input-group w-100">
                      {(() => {
                        const valStr = draftValues.targetValue;
                        const minStr = draftValues.targetMinValue;
                        const maxStr = draftValues.targetMaxValue;

                        const isDisabled =
                          !minStr || !maxStr || minStr === '' || maxStr === '';

                        const showError = (() => {
                          if (isDisabled || !valStr) return '';
                          const val = Number(valStr);
                          const min = Number(minStr);
                          const max = Number(maxStr);
                          return val < min || val > max
                            ? 'form-input-error'
                            : '';
                        })();

                        return (
                          <input
                            className={`quality-check-input-field text-sm ${showError}`}
                            placeholder="Enter value"
                            type="number"
                            disabled={isDisabled}
                            onWheel={(e) => e.target.blur()}
                            value={valStr || ''}
                            onChange={(e) =>
                              setDraftValues((prev) => ({
                                ...prev,
                                targetValue: e.target.value,
                              }))
                            }
                          />
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
              {/* VALUE Mode */}
              {numericType === 'value' && (
                <Col xxl={12} xl={12} lg={12}>
                  <div>
                    <span className="text-sm">Target value</span>
                    <div className="quality-check-input-group w-100">
                      <input
                        className="quality-check-input-field text-sm"
                        placeholder="Enter value"
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        value={draftValues.targetValue}
                        onChange={(e) =>
                          setDraftValues((prev) => ({
                            ...prev,
                            targetValue: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </Col>
              )}
            </Row>
            <div className="div-flex-column div-align-center padding-top-8">
              {(() => {
                const min = Number(draftValues.targetMinValue);
                const max = Number(draftValues.targetMaxValue);
                const val = Number(draftValues.targetValue);

                const hasError =
                  (draftValues.targetMinValue &&
                    draftValues.targetMaxValue &&
                    max < min) ||
                  (numericType === 'range' &&
                    draftValues.targetValue &&
                    (val < min || val > max));
                return (
                  <button
                    className={`common-btn theme-background-${themeColor}`}
                    type="button"
                    disabled={hasError}
                    onClick={() => {
                      handleAddDimensionValue(activeDimension.id, draftValues);
                      setAddTargetValueModal(false);
                    }}
                  >
                    Save
                  </button>
                );
              })()}
            </div>
          </div>
        </Modal.Body>
      )}
      {activeDimension?.data_type?.id === 3 && (
        <Modal.Body>
          <div className="div-flex-column rg-10">
            <div className="div-flex-row div-space-between div-align-center padding-bottom-10 border-bottom cg-10">
              <div className="div-flex-row div-align-center cg-5 quality-check-icon-padding border-radius-4">
                <StringIcon
                  className="icon-size-big"
                  color={getColorCode(themeColor)}
                />
                <span className="text-lg text-semi-bold">
                  {activeDimension?.data_type?.name}
                </span>
              </div>
              <button
                className="icon-btn-none"
                onClick={() => setAddTargetValueModal(false)}
                aria-label="Close"
              >
                <Close className="quality-check-modal-icon" />
              </button>{' '}
            </div>
            <Row>
              <Col xxl={12} xl={12} lg={12} className=" div-flex-column rg-5">
                <span className="text-sm">Target value</span>
                <div>
                  <textarea
                    className="form-input-description"
                    placeholder="Enter value"
                    type="text"
                    rows={2}
                    value={
                      formData.dimensions?.find(
                        (dimensionObj) =>
                          dimensionObj.dimension_id === activeDimension.id,
                      )?.targetValue || ''
                    }
                    onChange={(e) =>
                      handleAddDimensionValue(activeDimension.id, {
                        targetValue: e.target.value,
                      })
                    }
                  />
                </div>
              </Col>
            </Row>
            <div className="div-flex-column div-align-center padding-top-8">
              <button
                className={`common-btn theme-background-${themeColor}`}
                type="button"
                onClick={() => setAddTargetValueModal(false)}
              >
                Save
              </button>
            </div>
          </div>
        </Modal.Body>
      )}
      {activeDimension?.data_type?.id === 4 && (
        <Modal.Body>
          <div className="div-flex-column rg-10">
            {/* Header */}
            <div className="div-flex-row div-space-between div-align-center padding-bottom-10 border-bottom cg-10">
              <div className="div-flex-row div-align-center cg-5 quality-check-icon-padding border-radius-4">
                <EmptyCheckBoxIcon
                  className="icon-size-lg"
                  color={getColorCode(themeColor)}
                />
                <FilleCheckBox
                  className="icon-size-lg"
                  color={getColorCode(themeColor)}
                />
                <span className="text-lg text-semi-bold">
                  {activeDimension?.data_type?.name}
                </span>
              </div>
              <button
                className="icon-btn-none"
                onClick={() => setAddTargetValueModal(false)}
                aria-label="Close"
              >
                <Close className="quality-check-modal-icon" />
              </button>{' '}
            </div>{' '}
            <span className="text-sm">Select valid options</span>
            <div
              className={`quality-check-modal-height div-flex-column rg-10 theme-scrollable-container-${themeColor}`}
            >
              {activeDimension?.available_values?.map((option) => {
                const currentOptions =
                  formData.dimensions?.find(
                    (dimensionObj) =>
                      dimensionObj.dimension_id === activeDimension.id,
                  )?.targetOption || [];
                return (
                  <div key={option.id} className="div-flex-column cg-10">
                    <div className="quality-check-list-data div-flex-row div-space-between border border-radius-4">
                      <span className="text-sm text-grey">{option.name}</span>
                      <input
                        type="checkbox"
                        checked={currentOptions.includes(option.id)}
                        onChange={(e) =>
                          handleAddDimensionValue(activeDimension.id, {
                            targetOption: getUpdatedOptions(
                              e,
                              currentOptions,
                              option.id,
                            ),
                          })
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="div-flex-column div-align-center padding-top-8">
              <button
                className={`common-btn theme-background-${themeColor}`}
                type="button"
                onClick={() => setAddTargetValueModal(false)}
              >
                Save
              </button>
            </div>
          </div>
        </Modal.Body>
      )}
      {activeDimension?.data_type?.id === 5 && (
        <Modal.Body>
          {(() => {
            const currentBoolean =
              formData.dimensions?.find(
                (dimensionObj) =>
                  dimensionObj.dimension_id === activeDimension?.id,
              )?.targetBoolean ?? null;

            return (
              <div className="div-flex-column rg-10">
                <div className="div-flex-row div-space-between div-align-center padding-bottom-10 border-bottom cg-10">
                  <div className="div-flex-row div-align-center cg-5 quality-check-icon-padding border-radius-4">
                    <ToggleIcon
                      className="icon-size-big"
                      color={getColorCode(themeColor)}
                    />
                    <span className="text-lg text-semi-bold">
                      {activeDimension?.data_type?.name}
                    </span>
                  </div>
                  <button
                    className="icon-btn-none"
                    onClick={() => setAddTargetValueModal(false)}
                    aria-label="Close"
                  >
                    <Close className="quality-check-modal-icon" />
                  </button>{' '}
                </div>
                <span className="text-sm">Select valid options</span>
                <div className="quality-check-list-data div-flex-row div-align-center div-space-between border border-radius-4">
                  <span className="text-sm text-grey">
                    The quality check condition is
                  </span>
                  <div className="div-flex-row div-align-center cg-10">
                    <span className="text-md text-green bg-green quality-check-boolean-padding border-green border-radius-4">
                      True
                    </span>
                    <input
                      type="checkbox"
                      className="quality-check-checkbox"
                      checked={currentBoolean === true}
                      onChange={(e) =>
                        handleAddDimensionValue(activeDimension.id, {
                          targetBoolean: e.target.checked ? true : null,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="quality-check-list-data div-flex-row div-align-center div-space-between border border-radius-4">
                  <span className="text-sm text-grey">
                    The quality check condition is
                  </span>
                  <div className="div-flex-row div-align-center cg-10">
                    <span className="text-md text-red quality-check-bg-red quality-check-boolean-padding border-red border-radius-4">
                      False
                    </span>
                    <input
                      type="checkbox"
                      className="quality-check-checkbox"
                      checked={currentBoolean === false}
                      onChange={(e) =>
                        handleAddDimensionValue(activeDimension.id, {
                          targetBoolean: e.target.checked ? false : null,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="div-flex-column div-align-center padding-top-8">
                  <button
                    className={`common-btn theme-background-${themeColor}`}
                    type="button"
                    onClick={() => setAddTargetValueModal(false)}
                  >
                    Save
                  </button>
                </div>
              </div>
            );
          })()}
        </Modal.Body>
      )}
    </Modal>
  );
}
